/**
 * What is still unfinished in a staff delivery choice before it can be sent
 * (order to invoice doc 104 §5.3, §9.1). The catalogue refuses an agreed price
 * without its reason, and a weight without its parcels, so a half-entered pair
 * must not be sent; the words for what is wrong are the server's, said when it
 * refuses a submit. These are only the checks that stop an obviously
 * incomplete submit, with no sentence of their own.
 */

import type { CatalogueDelivery } from "../data/catalogue-types";
import type { DeliverySelection } from "../data/delivery-types";

export interface DeliverySelectionGaps {
  agreedPrice: boolean;
  agreedReason: boolean;
  packages: boolean;
}

export function deliverySelectionGaps(
  selection: DeliverySelection,
): DeliverySelectionGaps {
  const isAgreed = selection.isAgreedPrice;
  return {
    agreedPrice: isAgreed && selection.agreedDeliveryPricePence == null,
    agreedReason: isAgreed && !selection.agreedDeliveryReason?.trim(),
    packages: (selection.weightKg == null) !== (selection.parcels == null),
  };
}

/** True while an agreed price lacks its price or its reason, or a weight its parcels (or the reverse). */
export function isDeliverySelectionUnfinished(
  selection: DeliverySelection,
): boolean {
  const gaps = deliverySelectionGaps(selection);
  return gaps.agreedPrice || gaps.agreedReason || gaps.packages;
}

/** "9.80", "£9.80" or "9.8" → 980 pence; null when it isn't a price. */
export function penceOfPounds(pounds: string): number | null {
  const text = pounds.trim().replace(/^£/, "").replace(/,/g, "");
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return null;
  return Math.round(Number(text) * 100);
}

/** Multi-destination with at least one row's address chosen: something to price (doc 106 §3). */
export function hasSplitAddress(selection: DeliverySelection): boolean {
  return (
    selection.mode === "multi" &&
    (selection.deliveryLines ?? []).some((row) => row.deliveryAddressId != null)
  );
}

/**
 * How many of a split line's rows have an address and a quantity: the
 * "Allocated {x} of {y}" figure, and what the server adds up.
 */
export function splitAllocated(selection: DeliverySelection): number {
  return (selection.deliveryLines ?? []).reduce(
    (sum, row) => sum + (row.deliveryAddressId != null ? row.quantity : 0),
    0,
  );
}

/**
 * Why a split line can't be added or ordered yet, in the server's words (doc
 * 106 §3.1): no address chosen, or rows not adding up to the line. Null when
 * it can be.
 */
export function splitBlocker(
  selection: DeliverySelection,
  quantity: number,
): string | null {
  if (!hasSplitAddress(selection)) return "Choose the delivery address.";
  const placed = splitAllocated(selection);
  return placed === quantity
    ? null
    : `The quantities add up to ${placed.toLocaleString("en-GB")}, not ${quantity.toLocaleString("en-GB")}.`;
}

/**
 * The delivery is not yet priced because an item has no weight or packing,
 * so staff may enter its weight and parcels (doc 104 §9.1, Q6): the server's
 * reason code `profile_missing`.
 */
export function isMissingWeightOrPacking(
  delivery: CatalogueDelivery | null,
): boolean {
  return (
    delivery?.status === "not_yet_priced" &&
    delivery.notPricedReason === "profile_missing"
  );
}
